import { useState } from 'react';
import { Shell } from '../../components/layout/Shell.jsx';
import { Bar } from '../../components/ui/Bar.jsx';
import { Btn } from '../../components/ui/Btn.jsx';
import { Card } from '../../components/ui/Card.jsx';
import { useSession } from '../../api/session.jsx';
import { telechargeLaSauvegarde } from '../../lib/sauvegarde';
import { C } from '../../theme/palette';
import { termesOffre } from '../../lib/terminologie';

/**
 * Archives : la sauvegarde des données et l'accès au classeur.
 *
 * <p>Cette page dressait sa propre liste des documents archivés, avec ses
 * filtres par type et par année. Elle doublait le classeur de la comptabilité,
 * qui range les mêmes pièces par type et les ouvre en A4 imprimable ; ici elles
 * n'étaient que des lignes, sans aperçu ni impression, et portaient leur
 * numéro d'enregistrement — « 5 » — plutôt que leur référence.
 *
 * <p>Il reste donc ce que cette page est seule à faire : sortir une copie
 * complète des données, et dire où se consultent les documents.
 */
export function ArchivesPage({ onNav, soc, estMixte, univers, setUnivers }) {
  const { session } = useSession();
  // La sauvegarde porte les fiches d'employés entières : le serveur ne la
  // sert qu'au patron, et l'écran ne la propose qu'à lui.
  const estPatron = !session || session.role === 'patron';
  const [erreur, setErreur] = useState('');

  /**
   * Fait télécharger la sauvegarde complète que le serveur assemble.
   *
   * <p>La page n'exportait que cinq collections prises à l'écran — ni le
   * stock, ni l'équipe, ni les dépenses. Le fichier est désormais le même que
   * celui des réglages : toutes les données de la société.
   */
  const exporteTout = async () => {
    setErreur('');
    try {
      await telechargeLaSauvegarde();
    } catch (e) {
      setErreur(e?.message || 'Export indisponible pour le moment.');
    }
  };

  return (
    <Shell
      active="plus"
      onNav={onNav}
      soc={soc}
      pageTitle="🗄️ Archives & sauvegarde"
      estMixte={estMixte}
      univers={univers}
      setUnivers={setUnivers}
    >
      <div
        style={{
          background: C.amberL,
          border: '1px solid ' + C.amber + '28',
          borderRadius: 10,
          padding: '12px 14px',
          fontSize: 10,
          color: C.grey,
          marginBottom: 14,
          lineHeight: 1.6,
        }}
      >
        {/* SwissTech garde les pièces 15 ans, au-delà du minimum légal : le
            texte cite les deux, pour ne pas laisser croire que la loi exige 15 ans. */}
        📋 <b>Conservation légale : 15 ans</b> chez SwissTech (minimum légal 10 ans · art. 958f CO).
        {termesOffre(soc, { estMag: univers === 'magasin' }).feminin
          ? ' Tes offres acceptées, factures et chantiers sont conservés automatiquement.'
          : ' Tes devis acceptés, factures et chantiers sont conservés automatiquement.'}
      </div>

      <Card style={{ marginBottom: 12 }}>
        <Bar
          icon="💾"
          title="Sauvegarde complète"
          color={C.navy}
          sub="Télécharge toutes tes données en un fichier"
        />
        <div style={{ padding: 13 }}>
          <div style={{ fontSize: 10, color: C.grey, lineHeight: 1.6, marginBottom: 11 }}>
            Un fichier contenant toutes les données de ta société : clients, offres, factures,
            chantiers, équipe (paie, AVS et IBAN compris), stock, catalogue, dépenses, ventes,
            commandes, livraisons, rendez-vous, feuilles et congés. Garde-le en lieu sûr
            (ordinateur, clé USB, cloud) — c'est ta sécurité en cas de perte.
          </div>
          {estPatron ? (
            <Btn
              label="⬇ Exporter toutes les données (JSON)"
              color={C.navy}
              full
              onClick={exporteTout}
            />
          ) : (
            <div style={{ fontSize: 10, color: C.muted, lineHeight: 1.6 }}>
              La sauvegarde complète est réservée au patron.
            </div>
          )}
          {erreur && (
            <div style={{ fontSize: 10, color: C.red, marginTop: 8, lineHeight: 1.5 }}>
              {erreur}
            </div>
          )}
        </div>
      </Card>

      <Card>
        <Bar
          icon="📁"
          title="Consulter tes documents"
          color={C.teal}
          sub="Factures, devis, dépenses, fiches de paie…"
        />
        <div style={{ padding: 13 }}>
          <div style={{ fontSize: 10, color: C.grey, lineHeight: 1.6, marginBottom: 11 }}>
            Tous tes documents classés, avec aperçu A4 imprimable, se trouvent dans le classeur de
            la comptabilité (onglet 📁 Documents).
          </div>
          <Btn
            label="📁 Ouvrir le classeur (Comptabilité)"
            ghost
            color={C.teal}
            full
            onClick={() => onNav('compta', { onglet: 'classeur' })}
          />
        </div>
      </Card>
    </Shell>
  );
}
